import SolicitacaoChatApi from '@/Apis/Agendamento/Services/SolicitacaoChatApi'
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import { ISolicitacaoChatMessage, SolicitacaoChatMessageTypeEnum } from '@/types/interfaces/solicitacao-chat/solicitacao-chat-message.interface'
import EventBus from '@/plugins/EventBus'
import { chatConnectionService } from '@/services/chat-connection.service'
import Apis from '@/Apis/_'
import { Solicitacao } from '../../../types/interfaces/Solicitacao'
import { ISolicitacaoChat, ISolicitacaoChatAreaResponsavel } from '../../../types/interfaces/solicitacao-chat/solicitacao-chat.interface'
import { SolicitacaoChatAreaResponsavelEnum } from '../../../types/interfaces/solicitacao-chat/solicitacao-chat-area-responsavel.enum'

@Module({ namespaced: true, name: 'ChatAgendamentoStore' })
export default class ChatAgendamentoStore extends VuexModule {
  private open = false
  loading = false

  chatArea: SolicitacaoChatAreaResponsavelEnum | string = ''
  solicitationId = ''
  hospitalId = ''
  solicitation = {} as Solicitacao
  senderId = ''
  responsibleUserId?: string = ''
  responsibleUserName?: string = ''

  canEdit = true
  finished: boolean | null = false

  recipientName = ''
  recipientLastSeenDate = ''
  recipientOnline = false
  attachmentLoading = false

  messages: ISolicitacaoChatMessage[] = []

  @Action
  async sendMessage(data: ISolicitacaoChatMessage) {
    if (this.isNew) {
      await this.context.dispatch('startChat')
    }

    // Send a message of type open
    let messageType: SolicitacaoChatMessageTypeEnum
    if (data.anexoId) {
      messageType = SolicitacaoChatMessageTypeEnum.anexo
    } else {
      messageType = SolicitacaoChatMessageTypeEnum.texto
    }

    const message: ISolicitacaoChatMessage = {
      tipo: messageType,
      texto: data.texto,
    }

    const messageIndex = this.messages.length

    // Add the message before create in api
    const localMessage: Partial<ISolicitacaoChatMessage> = {
      texto: data.texto,
      tipoMensagem: { display: '', key: messageType },
      usuarioId: this.senderId,
      dataDeEnvio: new Date().toISOString(),
      anexoUrl: data.anexoUrl,
      anexoNome: data.anexoNome,
      anexoTipo: data.anexoTipo,
    }

    this.context.commit('addMessage', localMessage)

    if (data.anexoId) {
      message.anexoId = data.anexoId
      message.anexoNome = data.anexoNome
      message.anexoUrl = data.anexoUrl
      message.anexoTipo = data.anexoTipo
    }

    const res = await SolicitacaoChatApi.createMessage(this.solicitationId, message)
    if (!res.success) {
      EventBus.$emit('setSnackbar', { show: true, text: 'Erro ao enviar mensagem', icon: 'warning', color: '#C62828' })
      // eslint-disable-next-line no-throw-literal
      throw 'Erro ao enviar mensagem'
    }

    // After message creation update the created at date
    localMessage.dataDeEnvio = res.data.dataDeEnvio

    // After message creation update the created message
    this.context.commit('setMessage', { index: messageIndex, message: localMessage })

    await this.context.dispatch('markChatAsRead')

    return res.data
  }

  @Action
  async sendAttachment(file: File) {
    this.context.commit('setAttachmentLoading', true)
    const formData = new FormData()
    formData.append('laudos', file)
    const filetype = file.type

    const res = await Apis.Agendamento.SolicitacaoPendenciaApi.uploadDocumento(formData, this.solicitation.hospitalId, 'AnexoDoChatMedico')
    if (!res.success) {
      EventBus.$emit('setSnackbar', { show: true, text: 'Erro ao enviar mensagem', icon: 'warning', color: '#C62828' })
      this.context.commit('setAttachmentLoading', false)
      // eslint-disable-next-line no-throw-literal
      throw 'Erro ao enviar mensagem'
    }

    const resData = res.data as { id: string, nomeArquivo: string, url: string }[]
    const uploadedData = resData[0]

    this.sendMessage({
      tipo: SolicitacaoChatMessageTypeEnum.anexo,
      texto: '',
      anexoId: uploadedData.id,
      anexoNome: uploadedData.nomeArquivo,
      anexoUrl: uploadedData.url,
      anexoTipo: /(gif|jpg|jpeg|jp2|jpe|tif|tiff|png|webp|bmp)$/i.test(filetype) ? 'image' : 'any',
    })
    this.context.commit('setAttachmentLoading', false)
  }

  @Action
  async startChat() {
    // Send a message of type open
    let messageType: SolicitacaoChatMessageTypeEnum
    if (this.chatArea === SolicitacaoChatAreaResponsavelEnum.agendamento) {
      messageType = SolicitacaoChatMessageTypeEnum.inicioAgendamento
    } else if (this.chatArea === SolicitacaoChatAreaResponsavelEnum.orcamento) {
      messageType = SolicitacaoChatMessageTypeEnum.inicioOrcamento
    } else {
      messageType = SolicitacaoChatMessageTypeEnum.inicioAutorizacao
    }

    const message: ISolicitacaoChatMessage = {
      tipo: messageType,
      texto: 'Chat iniciado pelo Médico/Secretária',
    }

    const messageRes = await SolicitacaoChatApi.createMessage(this.solicitationId, message)
    if (!messageRes.success) {
      EventBus.$emit('setSnackbar', { show: true, text: 'Erro ao enviar mensagem', icon: 'warning', color: '#C62828' })
      // eslint-disable-next-line no-throw-literal
      throw 'Erro ao enviar mensagem'
    }

    this.context.commit('addMessage', messageRes.data)

    this.context.commit('setFinished', false)

    await this.context.dispatch('openConnection')

    return true
  }

  @Action
  async openChatModal(data: IOpenChatModalData) {
    const {
      solicitationId,
      recipientName,
      senderId,
      hospitalId,
    } = data

    this.context.commit('setOpen', true)
    this.context.commit('setSenderId', senderId)
    this.context.commit('setSolicitationId', solicitationId)
    this.context.commit('setHospitalId', hospitalId || '')
    this.context.commit('setRecipientName', recipientName)

    EventBus.$emit('setLoadingRequest', { status: '', text: 'Carregando...' })

    try {
      await this.context.dispatch('loadChatData')

      if (this.canMarkAsRead) {
        await this.context.dispatch('markChatAsRead')
      }

      if (this.canOpenConnection) {
        await this.context.dispatch('openConnection')
      }
      // eslint-disable-next-line no-empty
    } catch (err) { }

    EventBus.$emit('clearLoadingRequest')
  }

  @Action
  async loadChatData() {
    const res = await SolicitacaoChatApi.getChatBySolicitationId(this.solicitationId)
    if (res.success) {
      const data = res.data as ISolicitacaoChat
      this.context.dispatch('setChatData', data)
    }
  }

  @Action
  async setChatData(data: ISolicitacaoChat) {
    this.context.commit('setResponsibleUserId', data.usuarioBackofficeResponsavelId)
    this.context.commit('setResponsibleUserName', data.usuarioBackofficeResponsavelNome)

    if (data.usuarioBackofficeResponsavelNome) {
      this.context.commit('setRecipientName', data.usuarioBackofficeResponsavelNome)
    }

    if (data.areaResponsavel && data.chatFinalizado === false) {
      this.context.commit('setChatArea', (data.areaResponsavel as ISolicitacaoChatAreaResponsavel).key || '')
    }

    this.context.commit('setRecipientLastSeenDate', data.dataDaUltimaVisualizacaoDoBackoffice)
    this.context.commit('setMessages', data.mensagens)
    this.context.commit('setSolicitation', data.solicitacao)
    this.context.commit('setFinished', data.chatFinalizado !== undefined ? data.chatFinalizado : null)
  }

  @Action
  async markChatAsRead() {
    await SolicitacaoChatApi.markChatAsRead(this.solicitationId)
  }

  @Action
  async closeChatModal() {
    if (this.canMarkAsRead) {
      await this.context.dispatch('markChatAsRead')
    }

    await this.context.dispatch('closeConnection')
    this.context.commit('setOpen', false)
    this.context.dispatch('reset')
  }

  @Action
  async openConnection() {
    chatConnectionService.listenMessageEvent(data => this.context.commit('addMessage', data))

    chatConnectionService.listenCheckOnlineEvent((data) => {
      if (data) {
        this.context.commit('setRecipientOnline', data.backofficeOnline)
        if (data.dataUltimaVisualizacaoBackoffice) {
          this.context.commit('setRecipientLastSeenDate', data.dataUltimaVisualizacaoBackoffice)
        }
      }
    })

    chatConnectionService.listenChatDataEvent((data) => {
      if (data) {
        this.context.commit('setResponsibleUserId', data.usuarioBackofficeResponsavelId)
        this.context.commit('setResponsibleUserName', data.usuarioBackofficeResponsavelNome)
        this.context.commit('setSolicitation', data.solicitacao)
        this.context.commit('setFinished', data.chatFinalizado !== undefined ? data.chatFinalizado : null)
      }
    })

    await chatConnectionService.openConnection(this.solicitationId, this.senderId)
  }

  @Action
  async closeConnection() {
    await chatConnectionService.closeConnection()
  }

  @Mutation
  setOpen(value: boolean) {
    this.open = value
  }

  @Mutation
  setChatArea(value: SolicitacaoChatAreaResponsavelEnum) {
    this.chatArea = value
  }

  @Mutation
  setSolicitationId(value: string) {
    this.solicitationId = value
  }

  @Mutation
  setHospitalId(value: string) {
    this.hospitalId = value
  }

  @Mutation
  setSenderId(value: string) {
    this.senderId = value
  }

  @Mutation
  setRecipientName(value: string) {
    this.recipientName = value
  }

  @Mutation
  setRecipientLastSeenDate(value: string) {
    this.recipientLastSeenDate = value
  }

  @Mutation
  setRecipientOnline(value: boolean) {
    this.recipientOnline = value
  }

  @Mutation
  setAttachmentLoading(value: boolean) {
    this.attachmentLoading = value
  }

  @Mutation
  setResponsibleUserId(value: string) {
    this.responsibleUserId = value
  }

  @Mutation
  setResponsibleUserName(value: string) {
    this.responsibleUserName = value
  }

  @Mutation
  addMessage(value: ISolicitacaoChatMessage) {
    this.messages.push(value)
  }

  @Mutation
  setMessage(data: { index: number, message: ISolicitacaoChatMessage }) {
    this.messages.splice(data.index, 1, data.message)
  }

  @Mutation
  setMessages(value: ISolicitacaoChatMessage[]) {
    this.messages = value
  }

  @Mutation
  setCanEdit(value: boolean) {
    this.canEdit = value
  }

  @Mutation
  setSolicitation(value: Solicitacao) {
    this.solicitation = value
  }

  @Mutation
  setFinished(value: boolean) {
    this.finished = value
  }

  @Mutation
  setLoading(value: boolean) {
    this.loading = value
  }

  @Action
  reset() {
    this.context.commit('setChatArea', '')
    this.context.commit('setSolicitationId', '')
    this.context.commit('setRecipientName', '')
    this.context.commit('setRecipientLastSeenDate', '')
    this.context.commit('setRecipientOnline', false)
    this.context.commit('setMessages', [])
    this.context.commit('setResponsibleUserId', '')
    this.context.commit('setCanEdit', [])
    this.context.commit('setSolicitation', {})
  }

  // Getters

  get isOpen() {
    return this.open
  }

  get isNew() {
    return this.finished || this.finished == null
  }

  get canMarkAsRead() {
    return this.finished
  }

  get canOpenConnection() {
    return this.finished || this.finished === false
  }
}

interface IOpenChatModalData {
  solicitationId: string;
  recipientName: string;
  senderId: string;
  hospitalId?: string;
}
